// Admin plan config mutations: create / update / deactivate.
//
// Invalidation follows the shared admin pattern: invalidate the admin root,
// then refetch active queries so the open page reflects the change at once.
// The workspaces screen also reads plans (limit hints next to the workspace
// plan), so root-level invalidation keeps both surfaces fresh.
import { useMutation } from '@tanstack/react-query';

import { queryClient } from '../../../shared/query/queryClient.js';

import {
  ADMIN_QUERY_ROOT,
  createAdminPlan,
  deactivateAdminPlan,
  updateAdminPlan,
  type CreateAdminPlanInput,
  type UpdateAdminPlanInput,
} from '../api/admin.client.js';

async function invalidateAdminQueries(): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: [ADMIN_QUERY_ROOT] });
  await queryClient.refetchQueries({ queryKey: [ADMIN_QUERY_ROOT], type: 'active' });
}

export function useAdminPlansMutations() {
  const createMutation = useMutation({
    mutationFn: (input: CreateAdminPlanInput) => createAdminPlan(input),
    onSuccess: invalidateAdminQueries,
  });

  const updateMutation = useMutation({
    mutationFn: (input: { planId: string } & UpdateAdminPlanInput) =>
      updateAdminPlan(input.planId, input),
    onSuccess: invalidateAdminQueries,
  });

  const deactivateMutation = useMutation({
    mutationFn: (planId: string) => deactivateAdminPlan(planId),
    onSuccess: invalidateAdminQueries,
  });

  return { createMutation, updateMutation, deactivateMutation };
}
